import React from "react";
import { useState, useEffect } from "react";
import { Box, Paper, useTheme, IconButton, Link } from '@mui/material';
import { tokens } from "../../../assets/theme";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useNavigate } from "react-router-dom";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { svSE, enUS } from '@mui/x-data-grid/locales';
import { useIntl } from "react-intl";
import messages from "./messages";
import AxionControls from "../../../components/controls/AxionControls";
import { HideIfNoRole } from "../../../auth/authUi";

export default function MachinesGrid(props) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();
    const { formatMessage } = useIntl();
    const apiRef = useGridApiRef();
    const language = localStorage.getItem('language') || 'en';
    const translations = language === "sv" ? svSE : enUS;
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
    const [sortModelStorage, setSortModelStorage] = useState([
        { field: "machineName", sort: "asc" }
    ]);
    let columns = [
        {
            field: "machineName",
            headerName: formatMessage(messages.machineNameColumn),
            flex: 0.5,
            renderCell: ({ id, value, field }) => {
                return <Link href={`/machines/${id}`} sx={{ color: '#fff', textDecoration: 'underline' }}>{value}</Link>
            },
        },
        {
            field: "description",
            headerName: formatMessage(messages.descriptionColumn),
            flex: 2,
        },
        {
            field: "machineTypeName",
            headerName: formatMessage(messages.machineTypeColumn),
            flex: 2,
            valueGetter: ({ row }) => row.machineType?.machineTypeName
        },
        {
            field: "url",
            headerName: formatMessage(messages.urlColumn),
            flex: 2,
        },
        {
            field: "machineStatus",
            headerName: formatMessage(messages.machineStatusColumn),
            flex: 1,
        },
        {
            field: "view",
            headerName: "",
            flex: 1,
            renderCell: (params) => {
                return (
                    <>
                        <HideIfNoRole anyOf={[1, 2]}>
                            <IconButton
                                aria-label={formatMessage(messages.showMachine)}
                                size="small"
                                sx={{
                                    "&:hover": {
                                        backgroundColor: colors.primary[500],
                                    },
                                    backgroundColor: colors.primary[700],
                                    marginLeft: "10px",
                                }}
                                onClick={() => {
                                    props.handleEditSettingsClick(params.row.id);
                                }}
                            >
                                <SettingsOutlinedIcon fontSize="inherit" />
                            </IconButton>
                        </HideIfNoRole>
                    </>
                )
            },
        },
    ];

    const columnOrderLocalStorageName = 'machines-columnOrder';
    const columnWidthsLocalStorageName = 'machines-columnWidths';

    // Load persisted column order, widths, and sort order on mount
    function initColumnsFromLocalStorage(columns) {
        let columnsToFix = columns;
        // Load column order
        const savedColumnOrder = localStorage.getItem(columnOrderLocalStorageName);
        if (savedColumnOrder) {
            const orderArray = JSON.parse(savedColumnOrder);
            columnsToFix = reorderColumns(orderArray, columnsToFix);
        }

        // Load column widths
        const savedWidths = localStorage.getItem(columnWidthsLocalStorageName);
        if (savedWidths) {
            const widthMap = JSON.parse(savedWidths);
            columnsToFix = applyColumnWidths(widthMap, columnsToFix);
        }
        return columnsToFix;
    };

    // Reorder columns based on saved order
    const reorderColumns = (orderArray, columnsToFix) => {
        const reorderedColumns = orderArray.map((field) =>
            columnsToFix.find((col) => col.field === field)
        );
        reorderedColumns.push(...columnsToFix.filter((col) => !reorderedColumns.includes(col)));
        return reorderedColumns;
    };

    // Apply saved column widths
    const applyColumnWidths = (widthMap, columnsToFix) => {
        const updatedColumns = columnsToFix.map((col) => ({
            ...col,
            width: widthMap[col.field] || col.width,
            flex: widthMap[col.field] !== undefined ? undefined : col.flex,
        }));
        return updatedColumns;
    };

    // Handle column order change
    const handleColumnOrderChange = () => {
        if (!apiRef.current) {
            return;
        }
        const orderedColumns = apiRef.current.getVisibleColumns();
        const newColumnOrder = orderedColumns.map((col) => col.field);
        localStorage.setItem(columnOrderLocalStorageName, JSON.stringify(newColumnOrder));
    };

    // Handle column resize
    const handleColumnResize = (params) => {
        const { colDef, width } = params;
        const newWidths = JSON.parse(localStorage.getItem(columnWidthsLocalStorageName)) || {};
        newWidths[colDef.field] = width;
        localStorage.setItem(columnWidthsLocalStorageName, JSON.stringify(newWidths));
    };

    if (props.machineResult?.isLoading) {
        return <AxionControls.AxionBackdrop open={true} />
    }

    columns = initColumnsFromLocalStorage(columns);

    return (
        <Box padding={2} paddingTop={8}>
            <Paper
                sx={{
                    position: "relative",
                    borderRadius: "4px",
                }}
            >
                <Box
                    m="0px 0 0 0"
                    height="76vh"
                    sx={{
                        "& .MuiDataGrid-root": {
                            backgroundColor: colors.primary[400],
                            borderColor: `${colors.primary[700]} !important`,
                            borderRadius: "0px",
                            fontSize: "1.0rem",
                            border: "0 !important",
                        },
                        "& .MuiDataGrid-main": {
                            borderRadius: "0px",
                            border: "0 !important",
                            borderColor: `${colors.primary[700]} !important`,
                        },
                        "& .MuiDataGrid-cell": {
                            borderColor: `${colors.primary[700]} !important`,
                            borderWidth: "2px !important",
                        },
                        "& .name.column--cell": {
                            color: colors.greenAccent[300],
                        },
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: colors.primary[400],
                            fontSize: '1.0rem',
                            borderColor: `${colors.primary[700]} !important`,
                            borderWidth: "2px"
                        },
                        "& .MuiDataGrid-virtualScroller": {
                            backgroundColor: colors.primary[400],
                        },
                        "& .MuiDataGrid-footerContainer": {
                            borderTop: "none",
                            backgroundColor: colors.primary[600],
                        },
                        "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                            color: `${colors.gray[100]} !important`,
                        },
                        "& .super-app-theme--11": {
                            bgcolor: `${colors.redAccent[500]} !important`,
                        },
                        "& .super-app-theme--undefined-stop": {
                            bgcolor: `${colors.redAccent[500]} !important`,
                        },
                        "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIconButton": {
                            opacity: 0,
                            transition: "opacity 0.2s"
                        },
                        "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIconButton": {
                            opacity: 1
                        },
                    }}
                >
                    {(!props.machineResult?.isLoading) && <DataGridPro
                        apiRef={apiRef}
                        localeText={translations.components.MuiDataGrid.defaultProps.localeText}
                        onColumnResize={handleColumnResize}
                        onColumnOrderChange={handleColumnOrderChange}
                        rows={props.machineResult?.data}
                        sortModel={sortModelStorage}
                        onSortModelChange={(newModel) => setSortModelStorage(newModel)}
                        columns={columns}
                        columnVisibilityModel={columnVisibilityModel}
                        onColumnVisibilityModelChange={(newModel) =>
                            setColumnVisibilityModel(newModel)
                        }
                        slotProps={{
                            panel: {
                                sx: {
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                        backgroundColor: "#799c4a",
                                        opacity: 0.3,
                                    },
                                    "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": {
                                        backgroundColor: "#799c4a",
                                    },
                                    "& .MuiDataGrid-panelFooter .MuiButtonBase-root": {
                                        color: colors.primary[200],
                                        fontSize: "0.75rem",
                                        "&:hover": {
                                            color: colors.primary[300],
                                        },
                                    },
                                    "& .MuiFormLabel-root.Mui-focused": {
                                        color: colors.primary[200],
                                    },
                                },
                            }
                        }}
                    />}
                </Box>
            </Paper>
        </Box>
    );
}